import React, { useState } from 'react';
import { Auth } from '@aws-amplify/auth';
import { Stack, Text, Box } from '@tonic-ui/react';
import { useTranslation } from 'react-i18next';
import NotificationBanner from '../../components/notification-banner/notification-banner';

import {
  FormContainer,
  InputField,
  resetConfirmationCodeValidation,
  SubmitButton,
} from '../../components/forms';

import RouteLink from '../../components/route-link';
import CenteredLayout from '../../components/centered-layout';

type ResendConfirmationProps = {
  email: string;
  setSubmitting: (value: boolean) => boolean;
  resetForm: () => void;
};

const ResendConfirmation = (): JSX.Element => {
  const [formErrors, setFormErrors] = useState({
    error: '',
  });
  const [confirmationCodeSent, setConfirmationCodeSent] = useState(false);
  const [resentEmail, setResentEmail] = useState('');
  const { t } = useTranslation();

  const handleSubmit = async ({ email, setSubmitting, resetForm }: ResendConfirmationProps) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    try {
      await Auth.resendSignUp(email);
      setResentEmail(email);
      setConfirmationCodeSent(true);
    } catch (error) {
      const errMsg = !error.message ? error : error.message;
      console.error('Error: ', errMsg);

      setFormErrors({
        ...formErrors,
        error: errMsg,
      });
    }
    setSubmitting(false);
    resetForm();
  };

  return (
    <CenteredLayout>
      {confirmationCodeSent ? (
        <Stack spacing="8x">
          <Text size="xl" lineHeight="xl">
            {t('resendConfirmation.confirmation.title')}
          </Text>

          <Text size="md" lineHeight="lg">
            {t('resendConfirmation.confirmation.messageParagraph1', { email: resentEmail })}
          </Text>
          <Text size="md" lineHeight="lg">
            {t('resendConfirmation.confirmation.messageParagraph2')}
          </Text>
          <Box textAlign="center" mt={32}>
            <RouteLink href="/">{t('goBack')}</RouteLink>
          </Box>
        </Stack>
      ) : (
        <FormContainer
          id="resend-confirmation-form"
          customValidation={resetConfirmationCodeValidation}
          initialValues={{ email: '' }}
          submitAction={(formPayload) => handleSubmit(formPayload)}
        >
          <Stack spacing="8x">
            <Text size="xl" lineHeight="xl">
              {t('resendConfirmation.title')}
            </Text>
            {formErrors?.error && (
              <NotificationBanner
                name="resend-confirmation-form"
                severity="error"
                variant="outline"
                shouldOpen={true}
                bannerText={formErrors.error}
              />
            )}
            <Text size="md" lineHeight="lg">
              {t('resendConfirmation.message')}
            </Text>
            <Box>
              <InputField
                id="resend-confirm-email"
                type="email"
                label={t('email')}
                name="email"
                isRequired={true}
              />
            </Box>
            <SubmitButton label={t('submit')} id="resend-confirm-submit-btn" />
            <Box textAlign="center" mt={32}>
              <RouteLink href="/">{t('backToSignIn')}</RouteLink>
            </Box>
          </Stack>
        </FormContainer>
      )}
    </CenteredLayout>
  );
};

export default ResendConfirmation;
